import DOMPurify from 'dompurify';

/**
 * Sanitize HTML content to prevent XSS attacks
 * Uses DOMPurify to clean potentially dangerous HTML
 */
export const sanitizeHtml = (dirty: string): string => {
  if (!dirty) return '';

  return DOMPurify.sanitize(dirty, {
    // Allow common HTML tags for rich text content
    ALLOWED_TAGS: [
      'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
      'p', 'br', 'hr',
      'ul', 'ol', 'li',
      'strong', 'b', 'em', 'i', 'u', 's', 'strike',
      'a', 'span', 'div',
      'blockquote', 'pre', 'code',
      'table', 'thead', 'tbody', 'tr', 'th', 'td',
      'img'
    ],
    // Allow safe attributes
    ALLOWED_ATTR: [
      'href', 'target', 'rel',
      'src', 'alt', 'title', 'width', 'height',
      'class', 'style'
    ],
    // Force all links to open in new tab with security attributes
    ADD_ATTR: ['target', 'rel'],
    // Disallow dangerous URL schemes
    ALLOWED_URI_REGEXP: /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|sms|cid|xmpp):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i,
    // Remove script, style, and other dangerous elements
    FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed', 'form', 'input', 'button'],
    FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'onfocus', 'onblur']
  });
};

/**
 * Validate and sanitize a URL to prevent javascript: and data: attacks
 * Returns empty string if URL is invalid or dangerous
 */
export const sanitizeUrl = (url: string | undefined | null): string => {
  if (!url) return '';

  const trimmedUrl = url.trim();

  // Block dangerous URL schemes
  const dangerousSchemes = [
    'javascript:',
    'data:',
    'vbscript:',
    'file:'
  ];

  const lowerUrl = trimmedUrl.toLowerCase();
  for (const scheme of dangerousSchemes) {
    if (lowerUrl.startsWith(scheme)) {
      console.warn(`Blocked dangerous URL scheme: ${scheme}`);
      return '';
    }
  }

  // Allow relative URLs, http, https, mailto, tel
  const allowedSchemes = ['http://', 'https://', 'mailto:', 'tel:', '//', '/'];
  const isAllowed = allowedSchemes.some(scheme => lowerUrl.startsWith(scheme)) ||
                    !trimmedUrl.includes(':'); // relative URLs without scheme

  if (!isAllowed) {
    console.warn(`Blocked URL with unknown scheme: ${trimmedUrl}`);
    return '';
  }

  return trimmedUrl;
};

/**
 * Escape special characters for safe text display
 * Use this for plain text that should not contain HTML
 */
export const escapeHtml = (text: string): string => {
  if (!text) return '';

  const htmlEscapes: Record<string, string> = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#x27;',
    '/': '&#x2F;'
  };

  return text.replace(/[&<>"'/]/g, char => htmlEscapes[char]);
};

/**
 * Sanitize plain text input by removing HTML tags and dangerous content
 * Use this for text fields that should never contain HTML (names, emails, etc.)
 */
export const sanitizeText = (text: string | undefined | null): string => {
  if (!text) return '';

  // Use DOMPurify with no allowed tags to strip all HTML
  const stripped = DOMPurify.sanitize(text, {
    ALLOWED_TAGS: [], // No HTML tags allowed
    ALLOWED_ATTR: [], // No attributes allowed
    KEEP_CONTENT: true // Keep text content inside tags
  });

  // Additional cleanup: remove any remaining angle brackets and normalize whitespace
  return stripped
    .replace(/<[^>]*>/g, '') // Remove any remaining HTML-like patterns
    .replace(/\s+/g, ' ') // Normalize whitespace
    .trim();
};

/**
 * Sanitize an email address
 * Validates format and removes dangerous characters
 */
export const sanitizeEmail = (email: string | undefined | null): string => {
  if (!email) return '';

  // Basic sanitization: trim and lowercase
  const cleaned = email.trim().toLowerCase();

  // Remove any HTML tags
  const noHtml = sanitizeText(cleaned);

  // Basic email format validation (not strict, just removes obviously invalid)
  const emailRegex = /^[^\s@<>]+@[^\s@<>]+\.[^\s@<>]+$/;
  if (!emailRegex.test(noHtml)) {
    console.warn(`Invalid email format: ${noHtml}`);
    // Return the sanitized version anyway, let the backend validate
  }

  return noHtml;
};

/**
 * Sanitize imported learner data
 * Cleans all text fields to prevent XSS attacks from imported files
 */
export interface ImportedLearnerData {
  fullname: string;
  email: string;
  groupe?: string;
  formation?: string;
  dateInscription?: string;
  dateCompletion?: string;
}

export const sanitizeImportedLearner = (learner: ImportedLearnerData): ImportedLearnerData => {
  return {
    fullname: sanitizeText(learner.fullname),
    email: sanitizeEmail(learner.email),
    groupe: learner.groupe ? sanitizeText(learner.groupe) : undefined,
    formation: learner.formation ? sanitizeText(learner.formation) : undefined,
    dateInscription: learner.dateInscription ? sanitizeText(learner.dateInscription) : undefined,
    dateCompletion: learner.dateCompletion ? sanitizeText(learner.dateCompletion) : undefined
  };
};

/**
 * Parse a date string in DD/MM/YYYY format to a Date object
 */
const parseFrenchDate = (dateStr: string): Date | null => {
  if (!dateStr) return null;

  const match = dateStr.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!match) return null;

  const [, day, month, year] = match;
  const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));

  // Validate the date is real
  if (isNaN(date.getTime())) return null;

  return date;
};

/**
 * Validation result for imported learner
 */
export interface ValidationResult {
  isValid: boolean;
  errors: string[];
}

/**
 * Validate chronological coherence of dates
 * Completion date must be >= enrollment date
 */
export const validateLearnerDates = (
  dateInscription: string | undefined,
  dateCompletion: string | undefined
): ValidationResult => {
  const errors: string[] = [];

  // If no completion date, it's valid (in progress)
  if (!dateCompletion) {
    return { isValid: true, errors: [] };
  }

  // If completion date but no inscription date, that's suspicious but we'll allow it
  if (!dateInscription) {
    return { isValid: true, errors: [] };
  }

  const inscriptionDate = parseFrenchDate(dateInscription);
  const completionDate = parseFrenchDate(dateCompletion);

  // If we can't parse the dates, skip validation
  if (!inscriptionDate || !completionDate) {
    return { isValid: true, errors: [] };
  }

  // Completion must be >= inscription
  if (completionDate < inscriptionDate) {
    errors.push(`Date de completion (${dateCompletion}) anterieure a la date d'inscription (${dateInscription})`);
    return { isValid: false, errors };
  }

  return { isValid: true, errors: [] };
};

/**
 * Validate and sanitize a learner with full validation
 */
export interface ValidatedLearner extends ImportedLearnerData {
  validationErrors: string[];
  isValid: boolean;
}

export const validateAndSanitizeLearner = (learner: ImportedLearnerData): ValidatedLearner => {
  const sanitized = sanitizeImportedLearner(learner);
  const dateValidation = validateLearnerDates(sanitized.dateInscription, sanitized.dateCompletion);

  return {
    ...sanitized,
    validationErrors: dateValidation.errors,
    isValid: dateValidation.isValid
  };
};
